/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useState, useCallback } from "react";
import { Select, MenuItem, FormGroup, FormControlLabel, Checkbox, TextField } from "@mui/material";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";
import LabelledField from "components/labelled-field";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { SimplicityComposerTipTap } from "cms/rich-text/simplicity-composer-tiptap";

const MAILEERLITE_FIELDS = [
    "email",
    "name",
    "city",
    "company",
    "country",
    "lastName",
    "phone",
    "state",
    "z_i_p"
];

const REQUIRED_FIELDS = ["email", "name"];

const NewsletterCTAComponentEditor = () => {
    const { component, update } = useUpdateComponent();
    const { token } = useAuth();
    
    const [emailServiceProviderData, setEmailServiceProviderData] = useState([]);
    const [selectedEmailServiceProvider, setSelectedEmailServiceProvider] = useState(null);
    const [selectedFields, setSelectedFields] = useState([]);
    const [emailGroups, setEmailGroups] = useState([]);
    const [selectedEmailGroup, setSelectedEmailGroup] = useState(null);

    const onChangeEmailServiceProvider = (event) => {
        const selectedValue = event.target.value;
        update("email_service_provider", selectedValue);
        setSelectedEmailServiceProvider(selectedValue);
    };

    const onChangeEmailGroup = (event) => {
        const selectedValue = event.target.value;
        const valueToStore = selectedValue === "" ? null : selectedValue;
        update("email_group", valueToStore);
        setSelectedEmailGroup(valueToStore);
    };

    const handleFieldChange = (field) => (event) => {
        if (REQUIRED_FIELDS.includes(field)) {
            return;
        }
        
        const isChecked = event.target.checked;
        const updatedFields = selectedFields.map((fieldObj) => 
            fieldObj.name === field 
                ? { ...fieldObj, enabled: isChecked }
                : fieldObj
        );
        
        setSelectedFields(updatedFields);
        update("selected_fields", updatedFields);
    };

    const getEmailServiceProvider = useCallback(async () => {
        const emailServiceProvidersFilter = {
            active: true,
        };
        const emailServiceProvider = await SimpliCityAdminSDK.email.getServiceProviders(token, emailServiceProvidersFilter);
        setEmailServiceProviderData(emailServiceProvider?.providers || []);
    }, [token]);

    useEffect(() => {
        getEmailServiceProvider();
    }, [getEmailServiceProvider]);

    useEffect(() => {
        if (component?.email_service_provider) {
            setSelectedEmailServiceProvider(component.email_service_provider);
        }
    }, [component?.email_service_provider]);

    useEffect(() => {
        if (component?.email_group) {
            setSelectedEmailGroup(component.email_group);
        }
    }, [component?.email_group]);

    useEffect(() => {
        if (component && selectedFields.length === 0) {
            const componentFields = component?.selected_fields || [];
            
            const normalizedComponentFields = componentFields.map((field) => 
                typeof field === "string" ? { name: field, enabled: true } : field
            );
            
            const allFieldsWithState = MAILEERLITE_FIELDS.map((fieldName) => {
                const existingField = normalizedComponentFields.find((f) => f.name === fieldName);
                const isRequired = REQUIRED_FIELDS.includes(fieldName);
                
                // If field exists in component data, use it; otherwise create new with required fields enabled
                if (existingField) {
                    return existingField;
                } else {
                    return { 
                        name: fieldName, 
                        enabled: isRequired
                    };
                }
            });
            
            setSelectedFields(allFieldsWithState);
            
            // If no selected_fields exist in component data, initialize with required fields
            if (componentFields.length === 0) {
                const requiredFieldsData = allFieldsWithState.filter((field) => field.enabled);
                update("selected_fields", requiredFieldsData);
            }
        }
    }, [component, selectedFields.length, update]);

    useEffect(() => {
        if (emailServiceProviderData.length > 0 && !selectedEmailServiceProvider && !component?.email_service_provider) {
            const firstProvider = emailServiceProviderData[0];
            setSelectedEmailServiceProvider(firstProvider?.key);
            update("email_service_provider", firstProvider?.key);
        }
    }, [emailServiceProviderData, selectedEmailServiceProvider, component?.email_service_provider, update]);

    useEffect(() => {
        const getEmailGroups = async () => {
            if (component?.email_service_provider) {
                const emailGroups = await SimpliCityAdminSDK.email.getGroups(token, {
                    providerKey: component?.email_service_provider,
                    limit: 100,
                });
                if (emailGroups?.groups && emailGroups?.groups?.length > 0) {
                    setEmailGroups(emailGroups?.groups);
                }
            }
        };
        getEmailGroups();
    }, [component?.email_service_provider, token]);

    return (
        <div className='simplicity-editor-frame'>

            <LabelledField label="Heading">
                <TextField
                    fullWidth
                    value={component?.heading || ""}
                    onChange={(e) => update("heading", e.target.value)}
                    placeholder="Enter heading"
                />
            </LabelledField>
            <LabelledField label="Subheading">
                <TextField
                    fullWidth
                    value={component?.subheading || ""}
                    onChange={(e) => update("subheading", e.target.value)}
                    placeholder="Enter subheading"
                />
            </LabelledField>

            <LabelledField label="Description">
                <SimplicityComposerTipTap
                    richtext={component?.description || ""}
                    onChange={(value) => update("description", value)}
                />
            </LabelledField>

            <LabelledField label="Subscribe Label">
                <TextField
                    fullWidth
                    value={component?.subscribe_label || ""}
                    onChange={(e) => update("subscribe_label", e.target.value)}
                    placeholder="Enter subscribe label"
                />
            </LabelledField>

            <LabelledField label="Email Service Provider">
                <Select
                    labelId="emailServiceProvider-label"
                    id="emailServiceProvider-select"
                    sx={{ width: "400px" }}
                    value={selectedEmailServiceProvider || ""}
                    onChange={onChangeEmailServiceProvider}
                >
                    {emailServiceProviderData?.map((provider) => (
                        <MenuItem key={provider?.key} value={provider?.key}>{provider?.name}</MenuItem>
                    ))}
                </Select>
            </LabelledField>

            <LabelledField label="Email Group">
                <Select
                    labelId="emailGroup-label"
                    id="emailGroup-select"
                    sx={{ width: "400px" }}
                    value={selectedEmailGroup ?? ""}
                    onChange={onChangeEmailGroup}
                    disabled={!selectedEmailServiceProvider}
                    displayEmpty
                >
                    <MenuItem value="">None</MenuItem>
                    {emailGroups?.map((group) => (
                        <MenuItem key={group?.id} value={group?.id}>{group?.name}</MenuItem>
                    ))}
                </Select>
            </LabelledField>

            {selectedEmailServiceProvider === "mailerlite" && (
                <div>
                    <h4>Mailerlite Fields</h4>
                    <FormGroup>
                        {MAILEERLITE_FIELDS.map((field) => {
                            const isRequired = REQUIRED_FIELDS.includes(field);
                            const fieldObj = selectedFields.find((f) => f.name === field);
                            const isChecked = fieldObj ? fieldObj.enabled : false;
                            
                            return (
                                <FormControlLabel
                                    key={field}
                                    control={
                                        <Checkbox
                                            checked={isChecked}
                                            onChange={handleFieldChange(field)}
                                            name={field}
                                            disabled={isRequired}
                                        />
                                    }
                                    label={field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, " ")}
                                />
                            );
                        })}
                    </FormGroup>
                </div>
            )}


        </div>
    );
};

export { NewsletterCTAComponentEditor }; 